/**
 * Экран замка.
 *
 * Клавиатура своя, а не системное поле ввода: цифровое поле на телефоне
 * открывает разную клавиатуру в разных браузерах, а иногда и обычную — и
 * человек тычет в мелкие буквы, чтобы набрать четыре цифры. Своя раскладка
 * одинакова везде и попадает под большой палец.
 *
 * Пароль проверяется сам, как только набрана его длина: кнопка «Войти» здесь
 * лишний шаг, который человек делает десять раз в день.
 */

import { Delete, Fingerprint, KeyRound } from 'lucide-react';
import { useEffect, useState } from 'react';

import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui';

import { type BiometricsPort, systemBiometrics } from '../model/biometrics';
import { MIN_PIN_LENGTH } from '../model/pin';

interface LockScreenProps {
  /** Проверяет пароль. `false` — не подошёл. */
  onSubmit: (pin: string) => Promise<boolean>;
  /** Открыть по отпечатку. `false` — не подтвердилось. */
  onBiometrics?: () => Promise<boolean>;
  /** Запасной вход: пароль аккаунта. */
  onForgot: () => void;
  attempts: number;
  biometrics?: BiometricsPort;
  /** Предлагать ли отпечаток — человек мог отключить его в настройках. */
  biometricsEnabled?: boolean;
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

export function LockScreen({
  onSubmit,
  onBiometrics,
  onForgot,
  attempts,
  biometrics = systemBiometrics(),
  biometricsEnabled = true,
}: LockScreenProps) {
  const [pin, setPin] = useState('');
  const [wrong, setWrong] = useState(false);
  const [busy, setBusy] = useState(false);
  const [canUseBiometrics, setCanUseBiometrics] = useState(false);

  const tryBiometrics = async () => {
    if (!onBiometrics) return;

    setBusy(true);
    try {
      await onBiometrics();
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!biometricsEnabled || !onBiometrics) return;
    let cancelled = false;

    void biometrics.available().then((value) => {
      if (cancelled) return;
      setCanUseBiometrics(value);

      // Сразу предлагаем палец: человек открыл приложение, чтобы им
      // пользоваться, а не чтобы сначала нажать кнопку «войти по отпечатку».
      if (value) void tryBiometrics();
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- один раз при открытии
  }, []);

  const press = (digit: string): void => {
    if (busy) return;

    const next = `${pin}${digit}`;
    setPin(next);
    setWrong(false);

    if (next.length >= MIN_PIN_LENGTH) void check(next);
  };

  const check = async (value: string) => {
    setBusy(true);
    try {
      if (!(await onSubmit(value))) {
        setWrong(true);
        setPin('');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col items-center justify-between bg-surface px-6 pt-safe pb-safe"
      role="dialog"
      aria-modal="true"
      aria-label="Приложение закрыто паролем"
    >
      <div className="flex flex-1 flex-col items-center justify-center gap-6">
        <span className="flex size-14 items-center justify-center rounded-full bg-brand-soft text-brand-soft-text">
          <KeyRound size={26} aria-hidden />
        </span>

        <div className="space-y-1 text-center">
          <p className="text-[17px] font-semibold">Введите пароль</p>
          <p className="text-[13px] text-muted">
            {wrong
              ? attempts > 2
                ? `Пароль не подошёл. Попыток подряд: ${attempts}`
                : 'Пароль не подошёл'
              : 'Kairo закрыт паролем на этом устройстве'}
          </p>
        </div>

        {/* Точки вместо цифр: пароль виден соседу ровно так же, как и на экране
            блокировки телефона. */}
        <div className="flex gap-3" aria-hidden>
          {Array.from({ length: Math.max(MIN_PIN_LENGTH, pin.length) }, (_, index) => (
            <span
              key={index}
              className={cn(
                'size-3 rounded-full transition-colors duration-150',
                index < pin.length ? 'bg-brand' : 'bg-surface-3',
                wrong && 'bg-danger/60',
              )}
            />
          ))}
        </div>
      </div>

      <div className="w-full max-w-xs space-y-3 pb-6">
        <div className="grid grid-cols-3 gap-2">
          {KEYS.map((key) => (
            <KeypadButton key={key} label={key} onClick={() => press(key)} disabled={busy} />
          ))}

          {canUseBiometrics ? (
            <KeypadButton
              label="Открыть отпечатком"
              icon={<Fingerprint size={22} aria-hidden />}
              onClick={() => void tryBiometrics()}
              disabled={busy}
            />
          ) : (
            <span />
          )}

          <KeypadButton label="0" onClick={() => press('0')} disabled={busy} />

          <KeypadButton
            label="Стереть"
            icon={<Delete size={22} aria-hidden />}
            onClick={() => setPin((value) => value.slice(0, -1))}
            disabled={busy || pin === ''}
          />
        </div>

        <Button variant="ghost" block onClick={onForgot}>
          Забыли пароль?
        </Button>
      </div>
    </div>
  );
}

interface KeypadButtonProps {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}

function KeypadButton({ label, icon, onClick, disabled }: KeypadButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={cn(
        'flex h-14 items-center justify-center rounded-lg bg-surface-2 text-[22px]',
        'transition-transform duration-150 active:scale-95 disabled:opacity-40',
      )}
    >
      {icon ?? label}
    </button>
  );
}
